// src/roles/store/MaterialSummary.js

import { useEffect, useState } from "react";
import { db } from "../../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";

export default function MaterialSummary() {
  const user       = JSON.parse(localStorage.getItem("user") || "{}");
  const company_id = user.company_id || "";

  const [data,    setData]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState("");
  const [filter,  setFilter]  = useState("all"); // all | low | zero

  // eslint-disable-next-line react-hooks/exhaustive-deps
useEffect(() => { fetchSummary(); }, []);
  const fetchSummary = async () => {
    try {
      setLoading(true);

      // ── 0. items master — fetch once for category + is_active lookup ──
      // Fetch ALL items — legacy docs have company_id: "" so cannot use where filter
      // Multi-tenant rule: allow exact company match OR legacy empty string only
      const itemsSnap = await getDocs(
  query(collection(db, "items"), where("company_id", "==", company_id))
);
      const itemsMaster = {};
      itemsSnap.forEach(d => {
        const item          = d.data();
        const itemCompanyId = item.company_id; // raw — no coercion

        // ✅ exact match — belongs to this company
        // ✅ "" empty string — legacy doc from before multi-tenant was enforced
        // ❌ anything else — different company, null, undefined, corrupt
        if (itemCompanyId !== company_id && itemCompanyId !== "") return;

        const byId    = (item.item_id   || "").trim();
        const byDocId = d.id.trim();                    // ← Firestore doc ID (pre-migration key)
        const byCode  = (item.item_code || "").trim();
        if (byId)    itemsMaster[byId]    = item;
        if (byDocId) itemsMaster[byDocId] = item;
        if (byCode)  itemsMaster[byCode]  = item;
        // Name-based lookup — for MRFs with wrong item_id (migration artifact)
        const byName = (item.item_name || "").toLowerCase().trim();
        if (byName)  itemsMaster[`name:${byName}`] = item;
      });

      // ── 1. inventory_summary — current stock ──────────
      const invSnap = await getDocs(
        query(
          collection(db, "inventory_summary"),
          where("company_id", "==", company_id)
        )
      );
      const invMap = {};
      invSnap.forEach(d => {
        const inv = d.data();

        // item_id: use inv.item_id if available, else doc ID
        const itemId  = inv.item_id || d.id;

        // qty field: old docs use "qty", new docs use "current_stock"
        const inStock = Number(inv.qty || inv.current_stock || 0);

        // Skip docs with no item_name
        if (!inv.item_name) return;

        // ── Resolve from items master ──
        const masterItem = itemsMaster[itemId] || itemsMaster[(inv.item_code || "").trim()] || null;
        const resolvedCategory = (
          masterItem?.category ||
          masterItem?.item_category ||
          inv.category ||
          ""
        ).toUpperCase();

        // Skip inactive items
        if (masterItem && masterItem.is_active === false) return;

        // Skip TOOLS and ASSET categories
        const EXCLUDED_CATEGORIES = ["TOOLS", "ASSET", "ASSETS"];
        if (EXCLUDED_CATEGORIES.includes(resolvedCategory)) return;

        // Fallback: if category still empty, check item_code prefix
        if (!resolvedCategory) {
          const code = (inv.item_code || "").toUpperCase();
          if (code.startsWith("AS-") || code.startsWith("TL-") || code.startsWith("AST-")) return;
        }

        // ── Canonical ID — always use item_id from items master if available ──
        const canonicalId = masterItem?.item_id || itemId;

        // Deduplicate by canonical ID — keep higher stock record
        if (invMap[canonicalId] && invMap[canonicalId].in_stock >= inStock) return;

        invMap[canonicalId] = {
          item_id:        canonicalId,
          item_code:      inv.item_code  || masterItem?.item_code  || "",
          item_name:      inv.item_name  || masterItem?.item_name  || "",
          category:       resolvedCategory || inv.category || "",
          unit:           inv.unit       || masterItem?.unit       || "nos",
          in_stock:       inStock,
          defective:      Number(inv.defective_stock || 0),
          scrap:          Number(inv.scrap_stock     || 0),
          minimum_stock:  Number(inv.minimum_stock   || 0),
          pending_mrfs:   0,
          approved_total: 0,
        };
      });

      // ── 2. material_requests — pending + approved ─────
      const mrfSnap = await getDocs(
        query(
          collection(db, "material_requests"),
          where("company_id", "==", company_id)
        )
      );

      mrfSnap.forEach(d => {
        const mrf = d.data();
        if (!["approved", "pending", "partially_issued", "issued"].includes(mrf.status)) return;

        const sourceItems = mrf.reviewed_items || mrf.items || [];

        sourceItems.forEach(item => {
          const itemId = String(item.item_id || "").trim();
          if (!itemId) return;

          // If item not in inventory_summary yet, add it
          // BUT only MATERIAL or CONSUMABLE — resolve from items master
          const mrf_master     = itemsMaster[itemId] || itemsMaster[(item.item_code||"").trim()] || null;
          const canonicalMrfId = mrf_master?.item_id || itemId;

          if (!invMap[canonicalMrfId]) {
            const mrfMaster  = itemsMaster[itemId] ||
                               itemsMaster[(item.item_code || "").trim()] || null;
            
            const mrfCat     = (mrfMaster?.category || item.category || "").toUpperCase();
            const itemCode   = (item.item_code || "").toUpperCase();

            // Skip inactive items
            if (mrfMaster && mrfMaster.is_active === false) return;

            // Skip TOOLS and ASSET by category
            const EXCLUDED_CATEGORIES = ["TOOLS", "ASSET", "ASSETS"];
            if (EXCLUDED_CATEGORIES.includes(mrfCat)) return;

            // Skip by item_code prefix — catches legacy items without category
            if (itemCode.startsWith("AS-") ||
                itemCode.startsWith("TL-") ||
                itemCode.startsWith("AST-")) return;

            // Final safety net — scan itemsMaster by item_name
            // Catches MRFs with wrong item_id but correct item_name
            const itemNameLower = (item.item_name || "").toLowerCase().trim();
            const masterByName = itemsMaster[`name:${itemNameLower}`] || null;
            if (masterByName && masterByName.is_active === false) return;
            if (masterByName) {
              const catByName = (masterByName.category || "").toUpperCase();
              if (EXCLUDED_CATEGORIES.includes(catByName)) return;
            }

            invMap[canonicalMrfId] = {
              item_id:        canonicalMrfId,
              item_code:      item.item_code || mrf_master?.item_code || "",
              item_name:      item.item_name || mrf_master?.item_name || "",
              category:       mrfCat || "",
              unit:           item.unit || item.uom || mrf_master?.unit || "nos",
              in_stock:       0,
              defective:      0,
              scrap:          0,
              minimum_stock:  0,
              pending_mrfs:   0,
              approved_total: 0,
            };
          }

          const approvedQty  = Number(item.approved_qty || item.qty || 0);
          const issuedQty    = Number(item.issued_qty   || 0);
          const requestedQty = Number(item.qty          || 0);
          const pendingQty   = Math.max(approvedQty - issuedQty, 0);

          if (["approved", "partially_issued", "issued"].includes(mrf.status)) {
            invMap[canonicalMrfId].approved_total += issuedQty || approvedQty;
          }
          if (mrf.status === "pending") {
            invMap[canonicalMrfId].pending_mrfs += requestedQty;
          }
          if (mrf.status === "partially_issued" && pendingQty > 0) {
            invMap[canonicalMrfId].pending_mrfs += pendingQty;
          }
        });
      });

      setData(Object.values(invMap).sort((a, b) => a.item_name.localeCompare(b.item_name)));
    } catch (err) {
      console.error("MaterialSummary fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Derived: stock status ──────────────────────────────
  const getStatus = (item) => {
    if (item.in_stock === 0)                                      return "zero";
    if (item.minimum_stock > 0 && item.in_stock <= item.minimum_stock) return "low";
    return "ok";
  };

  const STATUS_UI = {
    zero: { label: "Out of Stock", bg: "#fee2e2", color: "#991b1b", dot: "#ef4444" },
    low:  { label: "Low Stock",    bg: "#fef9c3", color: "#854d0e", dot: "#eab308" },
    ok:   { label: "In Stock",     bg: "#dcfce7", color: "#166534", dot: "#22c55e" },
  };

  // ── Filter + Search ────────────────────────────────────
  const filtered = data.filter(item => {
    const matchSearch = item.item_name.toLowerCase().includes(search.toLowerCase()) ||
                        item.item_code.toLowerCase().includes(search.toLowerCase());
    const status = getStatus(item);
    const matchFilter = filter === "all" || filter === status;
    return matchSearch && matchFilter;
  });

  // ── Counts for filter tabs ─────────────────────────────
  const counts = {
    all:  data.length,
    low:  data.filter(i => getStatus(i) === "low").length,
    zero: data.filter(i => getStatus(i) === "zero").length,
  };

  if (loading) return <div className="p-6 text-slate-400">Loading inventory...</div>;

  return (
    <div className="p-4 md:p-6 bg-gray-50 min-h-screen">

      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
        <h1 className="text-2xl font-semibold">Material Summary</h1>
        <button
          onClick={fetchSummary}
          className="px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
          ↻ Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <StatCard label="Total Items"   value={data.length}                       color="#6366f1" />
        <StatCard label="In Stock"      value={data.filter(i => getStatus(i) === "ok").length}   color="#22c55e" />
        <StatCard label="Low Stock"     value={counts.low}                         color="#eab308" />
        <StatCard label="Out of Stock"  value={counts.zero}                        color="#ef4444" />
      </div>

      {/* Search + Filter */}
      <div className="flex flex-wrap gap-3 mb-4">
        <input
          type="text"
          placeholder="Search item name or code..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="border border-gray-200 rounded-xl px-4 py-2 text-sm w-full md:w-72 outline-none focus:ring-2 focus:ring-indigo-200"
        />
        <div className="flex gap-2">
          {[
            { key: "all",  label: `All (${counts.all})` },
            { key: "low",  label: `⚠ Low (${counts.low})` },
            { key: "zero", label: `🔴 Zero (${counts.zero})` },
          ].map(f => (
            <button key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-3 py-1.5 rounded-xl text-sm font-semibold border transition ${
                filter === f.key
                  ? "bg-indigo-600 text-white border-indigo-600"
                  : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">

        {/* Table Header */}
        <div className="hidden md:grid md:grid-cols-8 bg-gray-900 text-white px-4 py-3 text-xs font-semibold uppercase tracking-wide">
          <div className="col-span-2">Item</div>
          <div className="text-center">Category</div>
          <div className="text-center">In Stock</div>
          <div className="text-center">Defective</div>
          <div className="text-center">Pending MRFs</div>
          <div className="text-center">Issued (All)</div>
          <div className="text-center">Status</div>
        </div>

        {filtered.length === 0 ? (
          <div className="p-12 text-center text-gray-400">
            <div className="text-4xl mb-3">📦</div>
            No items found
          </div>
        ) : (
          filtered.map((item, i) => {
            const status = getStatus(item);
            const ui     = STATUS_UI[status];
            return (
              <div
                key={item.item_id}
                className={`border-b border-gray-50 px-4 py-3 ${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"}`}
                style={{ borderLeft: `3px solid ${ui.dot}` }}
              >
                {/* Mobile layout */}
                <div className="md:hidden space-y-2">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold text-sm text-gray-800">{item.item_name}</p>
                      <p className="text-xs text-gray-400">{item.item_code} {item.category && `• ${item.category}`}</p>
                    </div>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <MobileCell label="In Stock"    value={`${item.in_stock} ${item.unit}`}    bold />
                    <MobileCell label="Defective"   value={`${item.defective} ${item.unit}`} />
                    <MobileCell label="Pending MRF" value={`${item.pending_mrfs} ${item.unit}`}
                      highlight={item.pending_mrfs > 0} />
                  </div>
                </div>

                {/* Desktop layout */}
                <div className="hidden md:grid md:grid-cols-8 items-center text-sm">
                  <div className="col-span-2">
                    <p className="font-semibold text-gray-800">{item.item_name}</p>
                    <p className="text-xs text-gray-400">{item.item_code}</p>
                  </div>
                  <div className="text-center text-xs text-gray-500">{item.category || "—"}</div>
                  <div className="text-center font-semibold">{item.in_stock} <span className="text-xs text-gray-400">{item.unit}</span></div>
                  <div className="text-center text-red-500">{item.defective > 0 ? item.defective : "—"}</div>
                  <div className={`text-center font-semibold ${item.pending_mrfs > 0 ? "text-amber-600" : "text-gray-400"}`}>
                    {item.pending_mrfs > 0 ? item.pending_mrfs : "—"}
                  </div>
                  <div className="text-center text-gray-500">{item.approved_total || "—"}</div>
                  <div className="text-center">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full"
                      style={{ background: ui.bg, color: ui.color }}>
                      {ui.label}
                    </span>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <p className="text-xs text-gray-400 mt-3 text-right">
        {filtered.length} of {data.length} items • Last updated from live Firestore
      </p>
    </div>
  );
}

// ── Helper components ──────────────────────────────────────

function StatCard({ label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
      <div className="text-2xl font-bold" style={{ color }}>{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

function MobileCell({ label, value, bold, highlight }) {
  return (
    <div className="bg-gray-50 rounded-lg p-2 text-center">
      <div className="text-gray-400 text-[10px] mb-0.5">{label}</div>
      <div className={`text-xs ${bold ? "font-bold text-gray-800" : ""} ${highlight ? "text-amber-600 font-semibold" : "text-gray-600"}`}>
        {value}
      </div>
    </div>
  );
}